import { h, Component, Element, State, Prop, Watch, Listen, Event, EventEmitter } from '@stencil/core';

import { throttle } from '../../../services/utils';

/**
 * A segmented control is a horizontal arrangement of two or more segments, each functioning as a distinct button.
 * It is used as a way to offer multiple selections with a single control.
 *
 * @example
 * ```html
 * <pk-segmented-control value="2">
 *   <pk-segmented-control-item value="1">Option 1</pk-segmented-control-item>
 *   <pk-segmented-control-item value="2">Option 2</pk-segmented-control-item>
 * </pk-segmented-control>
 * ```
 *
 * @documentation https://b2c.uefa.design/4a7979wvg/p/9861a5-segmented-control/b/24a952
 * @status in-progress
 *
 * @attr {string} value - The value of the active segmented control.
 *
 * @event pkChange - Callback handler emitted when the value of the segmented control changes.
 *
 * @slot - The default slot for the segmented control items.
 *
 * @part base - The base wrapper container of the segmented control.
 *
 * @cssprop --segmented-control--bgcolor - The background color of the segmented control.
 * @cssprop --segmented-control--active-color - The background color of the active segmented control item.
 */
@Component({
  tag: 'pk-segmented-control',
  styleUrl: './scss/pk-segmented-control.scss',
  shadow: true,
})
export class PkSegmentedControl {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkSegmentedControlElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() private activeSegmentOffset = null;
  @State() private segmentWidth = null;

  // Public Property API
  // ========================

  /** The value of the active segmented control. */
  @Prop({ reflect: true, mutable: true }) value: string;

  // Prop lifecycle events
  // =======================

  @Watch('value')
  handleValueChange() {
    // Set the active state of the segmented control items
    this.segmentedControlItems.forEach((item, index) => {
      // If the value is set, set the active state based on the value prop
      // otherwise set the first item as active
      item.active = this.value ? item.value === this.value : index === 0;
    });

    requestAnimationFrame(() => {
      this.setActiveSegmentOffset();
    });
  }

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler emitted when the value of the segmented control changes. */
  @Event() pkChange: EventEmitter<{ el: HTMLPkSegmentedControlItemElement; value: string }>;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  componentWillLoad() {
    this.setSegmentWidth();
  }

  componentDidLoad() {
    this.handleValueChange();
  }

  // Listeners
  // ==============

  @Listen('pkClick')
  handlePkClick(event: CustomEvent) {
    const target = event.target as HTMLPkSegmentedControlItemElement;
    if (!this.el.contains(target)) return;

    this.value = target.value;
    // Emit the change event
    this.pkChange.emit({ el: target, value: target.value });
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
    this.refreshActiveSegmentPosition();
  }

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private readonly setSegmentWidth = () => {
    this.segmentWidth = this.segmentedControlItems[0].getBoundingClientRect().width;
  };

  private readonly setActiveSegmentOffset = () => {
    const activeSegment = this.segmentedControlItems.find((item) => item.active);
    if (!activeSegment) return;

    const containerRect = this.el.getBoundingClientRect();
    const targetRect = activeSegment.getBoundingClientRect();

    // Calculate the offset of the active segment in pixels
    this.activeSegmentOffset = targetRect.left - containerRect.left;
  };

  private readonly refreshActiveSegmentPosition = throttle(() => {
    this.setSegmentWidth();
    this.setActiveSegmentOffset();
  }, 100);

  private get segmentedControlItems() {
    return Array.from(this.el.querySelectorAll('pk-segmented-control-item'));
  }

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const style = {
      '--segment-control--width': `${this.segmentWidth}px`,
      '--segment--control--active-offset': `${this.activeSegmentOffset}px`,
    };

    return (
      <div
        class="pk-segmented-control relative flex w-full rounded-[--segmented-control--border-radius] bg-[--segmented-control--bgcolor] p-xs3"
        style={style}
        role="group"
        part="base"
      >
        <slot onSlotchange={this.setSegmentWidth} />
      </div>
    );
  }
}
